import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Robot, MagnifyingGlass, CheckCircle, XCircle, LinkSimple, Globe, MapPin, ArrowSquareOut, ShieldCheck, Handshake, EnvelopeSimple, Sparkle, Planet, Clock, PlayCircle, PauseCircle, PlusCircle,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function AdminCrawler() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [url, setUrl] = useState("");
  const [crawling, setCrawling] = useState(false);
  const [queue, setQueue] = useState([]);
  const [claims, setClaims] = useState([]);
  const [genCountry, setGenCountry] = useState("");
  const [genCount, setGenCount] = useState(10);
  const [generating, setGenerating] = useState(false);

  const load = () => {
    api.get("/crawler/queue").then((r) => setQueue(r.data)).catch(() => {});
    api.get("/admin/claims").then((r) => setClaims(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (user && user.role !== "admin") { nav("/dashboard"); return; }
    load();
  }, [user]);

  const crawl = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setCrawling(true);
    try {
      const { data } = await api.post("/crawler/crawl", { url });
      toast.success(`Extracted "${data.item.name}" · ${data.discovered_links.length} alumni links found`);
      setUrl(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCrawling(false); }
  };

  const publish = async (id) => { await api.post(`/crawler/queue/${id}/publish`); toast.success("Published — institution page + alumni association group created"); load(); };
  const reject = async (id) => { await api.delete(`/crawler/queue/${id}`); toast.success("Rejected"); load(); };
  const generate = async (e) => {
    e.preventDefault();
    if (!genCountry.trim()) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/institutions/generate", { country: genCountry.trim(), count: Number(genCount) || 10 });
      toast.success(`${data.queued} institution${data.queued !== 1 ? "s" : ""} generated for ${data.country} — review below`);
      setGenCountry(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setGenerating(false); }
  };
  const approveClaim = async (id) => { await api.post(`/admin/claims/${id}/approve`); toast.success("Claim approved — user can now manage the institution"); load(); };
  const rejectClaim = async (id) => { await api.post(`/admin/claims/${id}/reject`); toast.success("Claim rejected"); load(); };

  return (
    <main className="mx-auto max-w-5xl px-5 py-8">
      <div className="flex items-center gap-2"><ShieldCheck className="text-secondary" size={22} weight="duotone" /><p className="overline text-secondary">Admin · moderation</p></div>
      <h1 className="mt-1 font-heading text-3xl font-light tracking-tight sm:text-4xl">Admin Console</h1>

      <Tabs defaultValue="crawler" className="mt-6">
        <TabsList>
          <TabsTrigger value="crawler" data-testid="admin-tab-crawler"><Robot size={16} className="mr-1.5" /> Site crawler <Badge variant="secondary" className="ml-2 rounded-full">{queue.length}</Badge></TabsTrigger>
          <TabsTrigger value="claims" data-testid="admin-tab-claims"><Handshake size={16} className="mr-1.5" /> Institution claims <Badge variant="secondary" className="ml-2 rounded-full">{claims.length}</Badge></TabsTrigger>
          <TabsTrigger value="discovery" data-testid="admin-tab-discovery"><Planet size={16} className="mr-1.5" /> Global discovery</TabsTrigger>
        </TabsList>

        <TabsContent value="crawler" className="mt-6">
          <Card className="border-secondary/30 bg-secondary/5 p-6 shadow-sm" data-testid="ai-generate-card">
            <div className="flex items-center gap-2"><Sparkle size={18} weight="duotone" className="text-secondary" /><h3 className="font-heading text-lg font-medium">Generate institutions by country</h3></div>
            <p className="mt-1 text-sm text-muted-foreground">Use AI to draft a list of real institutions for a country. They land in the review queue below — publish the ones you want and each gets an official alumni association group.</p>
            <form onSubmit={generate} className="mt-4 flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input value={genCountry} onChange={(e) => setGenCountry(e.target.value)} placeholder="e.g. Canada, Nigeria, Japan" className="pl-11 rounded-full" data-testid="generate-country" />
              </div>
              <Input type="number" min={1} max={25} value={genCount} onChange={(e) => setGenCount(e.target.value)} className="w-full rounded-full sm:w-24" data-testid="generate-count" />
              <Button type="submit" className="rounded-full gap-2" disabled={generating} data-testid="generate-submit"><Sparkle size={16} /> {generating ? "Generating…" : "Generate"}</Button>
            </form>
          </Card>

          <Card className="mt-4 border-border/60 p-6 shadow-sm">
            <form onSubmit={crawl} className="flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Robot className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.university.edu" className="pl-11 rounded-full" data-testid="crawler-url" />
              </div>
              <Button type="submit" className="rounded-full gap-2" disabled={crawling} data-testid="crawler-submit"><MagnifyingGlass size={16} /> {crawling ? "Crawling…" : "Crawl & extract"}</Button>
            </form>
          </Card>

          <div className="mt-6 space-y-4">
            {queue.length === 0 && <p className="text-sm text-muted-foreground">Nothing awaiting review. Crawl a site to get started.</p>}
            {queue.map((item) => (
              <Card key={item.id} className="border-border/60 p-6 shadow-sm animate-fade-up" data-testid="queue-item">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex gap-4">
                    {item.logo_url ? <img src={item.logo_url} alt={item.name} className="h-16 w-16 rounded-xl object-cover" /> : <div className="grid h-16 w-16 place-items-center rounded-xl bg-accent"><Globe size={24} className="text-muted-foreground" /></div>}
                    <div>
                      <div className="flex items-center gap-2"><h3 className="font-heading text-lg font-medium">{item.name}</h3>
                        {item.source === "suggestion"
                          ? <Badge className="rounded-full font-normal text-xs bg-secondary/15 text-secondary border-secondary/30" data-testid="badge-suggestion">Community suggestion</Badge>
                          : <Badge variant="outline" className="rounded-full font-normal text-xs">Unverified · community sourced</Badge>}
                      </div>
                      <Badge variant="secondary" className="mt-1 rounded-full font-normal">{item.type}</Badge>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        {item.location && <span className="flex items-center gap-1"><MapPin size={13} /> {item.location}</span>}
                        {item.country && <span className="flex items-center gap-1"><Globe size={13} /> {item.country}</span>}
                        {item.founded && <span>Founded {item.founded}</span>}
                        {item.website && <a href={item.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-secondary hover:underline"><ArrowSquareOut size={13} /> Website</a>}
                        {item.source_url && <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-secondary hover:underline"><ArrowSquareOut size={13} /> Source</a>}
                      </div>
                      {(item.submitter_name || item.submitter_email || item.institution_email) && (
                        <div className="mt-1.5 text-xs text-muted-foreground">
                          {item.submitter_name && <span>Suggested by <span className="font-medium text-foreground/80">{item.submitter_name}</span> </span>}
                          {item.submitter_email && <span>· {item.submitter_email} </span>}
                          {item.institution_email && <span>· institution notified: {item.institution_email}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="rounded-full gap-1.5" onClick={() => publish(item.id)} data-testid="queue-publish"><CheckCircle size={15} /> Publish</Button>
                    <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => reject(item.id)} data-testid="queue-reject"><XCircle size={15} /> Reject</Button>
                  </div>
                </div>
                {item.description && <p className="mt-3 line-clamp-3 text-sm text-foreground/80">{item.description}</p>}
                {item.discovered_links?.length > 0 && (
                  <div className="mt-4 rounded-lg bg-muted/50 p-3">
                    <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><LinkSimple size={13} /> Discovered alumni / association links (become groups on publish)</p>
                    <div className="flex flex-wrap gap-2">
                      {item.discovered_links.slice(0, 8).map((l, j) => {
                        const url = typeof l === "string" ? l : l.url;
                        const label = typeof l === "string" ? l.replace(/^https?:\/\//, "") : l.label;
                        return <a key={j} href={url} target="_blank" rel="noopener noreferrer" className="max-w-[240px] truncate rounded-full bg-background px-3 py-1 text-xs text-secondary hover:underline">{label}</a>;
                      })}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="claims" className="mt-6">
          <div className="space-y-4">
            {claims.length === 0 && <p className="text-sm text-muted-foreground">No pending institution claims.</p>}
            {claims.map((c) => (
              <Card key={c.id} className="border-border/60 p-6 shadow-sm animate-fade-up" data-testid="claim-item">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="font-heading text-lg font-medium">{c.institution_name}</h3>
                    <p className="mt-1 text-sm"><span className="font-medium">{c.user_name}</span> · {c.role_title}</p>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><EnvelopeSimple size={13} /> {c.work_email}</span>
                    </div>
                    {c.evidence && <p className="mt-2 text-sm text-foreground/80">{c.evidence}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="rounded-full gap-1.5" onClick={() => approveClaim(c.id)} data-testid="claim-approve"><CheckCircle size={15} /> Approve</Button>
                    <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => rejectClaim(c.id)} data-testid="claim-reject-admin"><XCircle size={15} /> Reject</Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="discovery">
          <DiscoveryTab />
        </TabsContent>
      </Tabs>
    </main>
  );
}

function DiscoveryTab() {
  const [data, setData] = useState(null);
  const [region, setRegion] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/crawler/status").then((r) => setData(r.data)).catch(() => setData({ targets: [], runs: [], pending_review: 0, auto_pending: 0 }));
  useEffect(() => { load(); }, []);

  const runNow = async (r = null) => {
    setBusy(true);
    try { await api.post("/admin/crawler/run", r ? { region: r } : {}); toast.success("Discovery started — refresh in a few seconds"); setTimeout(load, 6000); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const addTarget = async (e) => {
    e.preventDefault();
    if (!region.trim()) return;
    try { await api.post("/admin/crawler/targets", { region: region.trim() }); toast.success(`Added ${region.trim()} to discovery`); setRegion(""); load(); }
    catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
  };
  const toggle = async (id) => { await api.post(`/admin/crawler/targets/${id}/toggle`); load(); };

  if (!data) return <p className="mt-6 text-sm text-muted-foreground">Loading…</p>;
  const fmt = (t) => t ? new Date(t).toLocaleString() : "Never";

  return (
    <div className="mt-6 space-y-5" data-testid="discovery-tab">
      <Card className="border-secondary/30 bg-secondary/5 p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2"><Planet size={18} weight="duotone" className="text-secondary" /><h3 className="font-heading text-lg font-medium">Global Institution Discovery</h3></div>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">An AI crawler that automatically finds real educational institutions around the world on a schedule (every 6 hours), dedupes them, and queues new ones for your review. Approve them in the <strong>Site crawler</strong> tab — each becomes a page with its own alumni association group.</p>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><Clock size={13} /> Runs automatically every 6 hours · you can also run it now.</p>
          </div>
          <Button className="rounded-full gap-2" onClick={() => runNow()} disabled={busy} data-testid="crawler-run-now"><Sparkle size={16} /> {busy ? "Starting…" : "Run discovery now"}</Button>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { label: "Regions tracked", value: data.targets.length },
            { label: "Awaiting review", value: data.pending_review },
            { label: "From auto-crawler", value: data.auto_pending },
            { label: "Total institutions", value: data.total_institutions ?? "—" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-border/60 bg-background p-4">
              <div className="font-heading text-2xl font-medium">{s.value}</div>
              <div className="text-xs text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="border-border/60 p-6 shadow-sm">
        <h3 className="font-heading text-lg font-medium">Discovery regions</h3>
        <form onSubmit={addTarget} className="mt-3 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Add a country or region, e.g. Ghana" className="pl-11 rounded-full" data-testid="crawler-add-region" />
          </div>
          <Button type="submit" variant="outline" className="rounded-full gap-2" data-testid="crawler-add-target"><PlusCircle size={16} /> Add region</Button>
        </form>
        <div className="mt-4 divide-y divide-border/50">
          {data.targets.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5" data-testid={`crawler-target-${t.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{t.region}</span>
                  <Badge variant={t.status === "active" ? "secondary" : "outline"} className="rounded-full text-xs font-normal">{t.status}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">Last run: {fmt(t.last_run)} · {t.discovered_total || 0} queued all-time</p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" className="rounded-full" onClick={() => runNow(t.region)} disabled={busy}>Run</Button>
                <Button size="sm" variant="ghost" className="rounded-full gap-1.5" onClick={() => toggle(t.id)}>
                  {t.status === "active" ? <><PauseCircle size={15} /> Pause</> : <><PlayCircle size={15} /> Resume</>}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="border-border/60 p-6 shadow-sm">
        <h3 className="font-heading text-lg font-medium">Recent runs</h3>
        {data.runs.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No runs yet.</p> : (
          <div className="mt-3 divide-y divide-border/50">
            {data.runs.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm" data-testid="crawler-run">
                <span className="text-muted-foreground">{fmt(r.started_at)}</span>
                <span>{(r.regions || []).map((x) => x.region).join(", ") || "—"}</span>
                <span className="font-medium text-secondary">+{r.queued || 0} queued</span>
                <Badge variant="outline" className="rounded-full text-xs font-normal">{r.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
